import { Component } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, AbstractControl, ValidatorFn, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { SentryService } from 'src/app/services/sentry.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { UserService } from 'src/app/services/user.service';
import { getOrganization_organizations_by_pk, ApolloService } from 'src/gql-generated/generated';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { NgIf } from '@angular/common';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatDivider } from '@angular/material/divider';

@Component({
    selector: 'app-organization-config',
    templateUrl: './organization-config.component.html',
    styleUrl: './organization-config.component.scss',
    imports: [
        NgIf,
        MatIcon,
        MatButton,
        RouterLink,
        MatFormField,
        MatInput,
        ReactiveFormsModule,
        MatError,
        CdkTextareaAutosize,
        MatRadioGroup,
        MatRadioButton,
        MatDivider,
    ],
})
export class OrganizationConfigComponent {
    /**
     * Current organization id
     */
    organizationId = this.route.snapshot.paramMap.get('organizationId');

    /**
     * Loaded organization
     */
    organization?: getOrganization_organizations_by_pk;

    /**
     * Formgroup tracking required fields of an organization
     */
    organisationFormGroup = new FormGroup({
        baseModelSystemPrompt: new FormControl<string>(''),
        baseModelTemperature: new FormControl<number | undefined>(0.2, numberRangeValidator(0, 1)),
        titleGenerationSystemPrompt: new FormControl<string>(''),
        maxChunksLLM: new FormControl<number | undefined>(10, numberRangeValidator(1, 999999)),
        selectChunksSimilarityMargin: new FormControl<number | undefined>(0.4, numberRangeValidator(0, 1)),
        selectChunksRerankerMargin: new FormControl<number | undefined>(0.4, numberRangeValidator(0, 1)),
        selectChunksMinSimilarity: new FormControl<number | undefined>(0.1, numberRangeValidator(0, 1)),
        selectChunksMinReranker: new FormControl<number | undefined>(0.1, numberRangeValidator(0, 1)),
        addDocumentNameToChunks: new FormControl<boolean>(true, Validators.required),
    });

    /**
     * True when awaiting an async operation. Used to prevent the user from (accidentally) running a function twice.
     */
    awaitingAsyncOperation = false;

    constructor(
        public breakpoint: BreakpointService,
        private apollo: ApolloService,
        private route: ActivatedRoute,
        private user: UserService,
        private snackbar: SnackbarService,
        private router: Router,
        private sentry: SentryService,
    ) {
        this.loadOrganization();
    }

    /**
     * Load organization data
     */
    loadOrganization(): void {
        // Load organization if id is found in URL
        if (this.organizationId) {
            firstValueFrom(this.apollo.getOrganization({ id: this.organizationId })).then((data) => {
                if (data.data?.organizations_by_pk) {
                    // Store organization and load values into organisationFormGroup
                    this.organization = data.data.organizations_by_pk;
                    this.organisationFormGroup.patchValue({
                        baseModelSystemPrompt: this.organization.base_model_system_prompt,
                        baseModelTemperature: this.organization.base_model_temperature,
                        titleGenerationSystemPrompt: this.organization.title_generation_system_prompt,
                        maxChunksLLM: this.organization.max_chunks_llm,
                        addDocumentNameToChunks: this.organization.add_document_name_to_chunks,
                        selectChunksMinSimilarity: this.organization.select_chunks_min_similarity,
                        selectChunksMinReranker: this.organization.select_chunks_min_reranker,
                        selectChunksSimilarityMargin: this.organization.select_chunks_similarity_margin,
                        selectChunksRerankerMargin: this.organization.select_chunks_reranker_margin,
                    });
                }
            });
        }
    }

    /**
     * Attempt to save organization updates
     */
    async updateOrganization(): Promise<void> {
        if (!this.awaitingAsyncOperation) {
            this.awaitingAsyncOperation = true;
            this.organisationFormGroup.markAllAsTouched();
            if (this.organisationFormGroup.valid && this.organization) {
                try {
                    // Attempt organization update
                    const updateReponse = await firstValueFrom(this.apollo.updateOrganization({
                        id: this.organization.id,
                        updatedById: this.user.profile.id,
                        organizationUpdate: {
                            base_model_system_prompt: this.organisationFormGroup.controls.baseModelSystemPrompt.value ?? undefined,
                            base_model_temperature: this.organisationFormGroup.controls.baseModelTemperature.value ?? undefined,
                            title_generation_system_prompt: this.organisationFormGroup.controls.titleGenerationSystemPrompt.value ?? undefined,
                            max_chunks_llm: this.organisationFormGroup.controls.maxChunksLLM.value ?? undefined,
                            add_document_name_to_chunks: this.organisationFormGroup.controls.addDocumentNameToChunks.value,
                            select_chunks_min_similarity: this.organisationFormGroup.controls.selectChunksMinSimilarity.value ?? undefined,
                            select_chunks_min_reranker: this.organisationFormGroup.controls.selectChunksMinReranker.value ?? undefined,
                            select_chunks_similarity_margin: this.organisationFormGroup.controls.selectChunksSimilarityMargin.value ?? undefined,
                            select_chunks_reranker_margin: this.organisationFormGroup.controls.selectChunksRerankerMargin.value ?? undefined,
                        },
                    }));
                    // If update is successful, redirect the user to organizations page.
                    if (updateReponse.data?.update_organizations_by_pk?.id) {
                        this.snackbar.success($localize`Organisatie succesvol aangepast`);
                        this.router.navigate(['settings', 'organizations']);
                    }
                } catch (error) {
                    this.sentry.error(error);
                    this.snackbar.error($localize`Organisatie aanpassen mislukt`);
                }
            }
            this.awaitingAsyncOperation = false;
        }
    }
}

/**
 * Custom validator to validate if a formcontrol value is in a range of min and max
 * Used to simplyfing displaying mat-error basaed on the 'range' error
 * @param min minimum value (inclusive)
 * @param max maximum value (inclusive)
 * @returns validator that can be used in form controls
 */
export function numberRangeValidator(min: number, max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (control.value == null) {
            return null;
        }

        return control.value < min || control.value > max ?
                { range: true } :
            null;
    };
}